import { fetchInvestmentsAccess } from "@internal/auth/api";

import type {
  DashboardFamily,
  EquityDashboardRow,
  EquityDetailResponse,
  ETFDashboardRow,
  ETFDetailResponse,
  InstrumentCatalogResponse,
  InvestmentDetailResponse,
  InvestmentQuoteResponse,
  InvestmentRefreshResponse,
  InvestmentsCachePullOperationStatus,
  InvestmentsCachePullResponse,
  InvestmentsAccessResponse,
  InvestmentsDashboardResponse,
  InvestmentsListResponse,
  InvestmentsMacroResponse,
  InvestmentEventsResponse,
  InvestmentsRuntimeReloadResponse,
  InvestmentsRuntimeStatusResponse,
  InvestmentsPublicationStatus,
  Portfolio,
  PortfolioPositionDetail,
  PortfolioPositionDetailUpsertRequest,
  PortfolioResponse,
  PortfolioReviewEntry,
  PortfolioReviewEntryCreateRequest,
  PortfolioReviewListResponse,
  PortfolioUpsertRequest,
  WatchlistLiveQuotesResponse,
  ComparableMetric,
  ComparableValuationResponse,
} from "./types";

export async function fetchEquityComparables(ticker:string,metric?:ComparableMetric):Promise<ComparableValuationResponse>{
  const params=new URLSearchParams();if(metric)params.set("metric",metric);const suffix=params.toString()?`?${params.toString()}`:"";
  return requestJSON<ComparableValuationResponse>(`/api/investments/equities/${encodeURIComponent(ticker)}/comparables${suffix}`);
}

export async function fetchInvestmentEvents(tickers:string[]=[]):Promise<InvestmentEventsResponse>{
  const params=new URLSearchParams();if(tickers.length)params.set("tickers",tickers.join(","));const suffix=params.toString()?`?${params.toString()}`:"";return requestJSON<InvestmentEventsResponse>(`/api/investments/events${suffix}`);
}

type APIError = Error & {
  status?: number;
};

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json() as { error?: string; message?: string; };
      if (body.error) {
        message = body.error;
      } else if (body.message) {
        message = body.message;
      }
    } catch {
      // ignore decode failure and fall back to status text
    }
    const error = new Error(message) as APIError;
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

function normalizeDashboard(response: InvestmentsDashboardResponse): InvestmentsListResponse {
  const families: DashboardFamily[] = [...(response.equityFamilies || []), ...(response.etfFamilies || [])];
  const rows = [
    ...(response.equityRows || []).map((row: EquityDashboardRow) => ({ ...row, instrumentType: "EQUITY" as const })),
    ...(response.etfRows || []).map((row: ETFDashboardRow) => ({ ...row, instrumentType: "ETF" as const })),
  ];
  const tickerSet = new Set<string>();
  for (const family of families) {
    for (const ticker of family.tickers || []) {
      tickerSet.add(ticker);
    }
  }
  for (const row of rows) {
    tickerSet.add(row.ticker);
  }
  return {
    tickers: Array.from(tickerSet).sort(),
    families,
    rows,
    generatedAt: response.generatedAt,
  };
}

function normalizeEquityDetail(response: Omit<EquityDetailResponse, "instrumentType">): InvestmentDetailResponse {
  return {
    instrumentType: "EQUITY",
    ...response,
  };
}

function normalizeETFDetail(response: Omit<ETFDetailResponse, "instrumentType">): InvestmentDetailResponse {
  return {
    instrumentType: "ETF",
    series: {
      annual: [],
      quarterly: [],
    },
    ...response,
  };
}

export async function fetchInvestmentsDashboard(): Promise<InvestmentsDashboardResponse> {
  return requestJSON<InvestmentsDashboardResponse>("/api/investments/dashboard");
}

export async function fetchInvestmentsMacro(): Promise<InvestmentsMacroResponse> {
  return requestJSON<InvestmentsMacroResponse>("/api/investments/macro");
}

export async function fetchInvestmentsList(): Promise<InvestmentsListResponse> {
  const response = await fetchInvestmentsDashboard();
  return normalizeDashboard(response);
}

export async function fetchInstrumentCatalog(ticker: string): Promise<InstrumentCatalogResponse> {
  return requestJSON<InstrumentCatalogResponse>(`/api/investments/catalog/${encodeURIComponent(ticker)}`);
}

export async function fetchEquityDetail(ticker: string): Promise<InvestmentDetailResponse> {
  const response = await requestJSON<Omit<EquityDetailResponse, "instrumentType">>(`/api/investments/equities/${encodeURIComponent(ticker)}`);
  return normalizeEquityDetail(response);
}

export async function fetchETFDetail(ticker: string): Promise<InvestmentDetailResponse> {
  const response = await requestJSON<Omit<ETFDetailResponse, "instrumentType">>(`/api/investments/etfs/${encodeURIComponent(ticker)}`);
  return normalizeETFDetail(response);
}

export async function fetchInvestmentQuote(ticker: string): Promise<InvestmentQuoteResponse> {
  return requestJSON<InvestmentQuoteResponse>(`/api/investments/${encodeURIComponent(ticker)}/quote`);
}

export async function refreshInvestmentTicker(ticker: string): Promise<InvestmentRefreshResponse> {
  return requestJSON<InvestmentRefreshResponse>(`/api/investments/${encodeURIComponent(ticker)}/refresh`, {
    method: "POST",
  });
}

export async function fetchInvestmentsRuntimeStatus(): Promise<InvestmentsRuntimeStatusResponse> {
  return requestJSON<InvestmentsRuntimeStatusResponse>("/api/investments/runtime/status");
}

export async function fetchInvestmentsPublicationStatus(): Promise<InvestmentsPublicationStatus> {
  return requestJSON<InvestmentsPublicationStatus>("/api/investments/publication/status");
}

export async function reloadInvestmentsRuntime(): Promise<InvestmentsRuntimeReloadResponse> {
  return requestJSON<InvestmentsRuntimeReloadResponse>("/api/investments/runtime/reload", {
    method: "POST",
  });
}

export async function pullInvestmentsCache(): Promise<InvestmentsCachePullOperationStatus> {
  return requestJSON<InvestmentsCachePullOperationStatus>("/api/investments/cache/pull", {
    method: "POST",
  });
}

export async function fetchInvestmentsCachePullStatus(): Promise<InvestmentsCachePullOperationStatus> {
  return requestJSON<InvestmentsCachePullOperationStatus>("/api/investments/cache/pull/status");
}

export async function pullInvestmentTickerFromBucket(ticker: string): Promise<InvestmentsCachePullResponse> {
  return requestJSON<InvestmentsCachePullResponse>(`/api/investments/cache/tickers/${encodeURIComponent(ticker)}/pull`, {
    method: "POST",
  });
}

export async function fetchPortfolio(): Promise<PortfolioResponse> {
  return requestJSON<PortfolioResponse>("/api/investments/portfolio");
}

export async function fetchWatchlistLiveQuotes(tickers: string[]): Promise<WatchlistLiveQuotesResponse> {
  const normalized = tickers
    .map((ticker) => ticker.trim().toUpperCase())
    .filter((ticker, index, all) => ticker !== "" && all.indexOf(ticker) === index);
  const params = new URLSearchParams();
  params.set("tickers", normalized.join(","));
  return requestJSON<WatchlistLiveQuotesResponse>(`/api/investments/watchlist/live-quotes?${params.toString()}`);
}

export async function createPortfolio(payload: PortfolioUpsertRequest): Promise<Portfolio> {
  return requestJSON<Portfolio>("/api/investments/portfolio/portfolios", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function updatePortfolio(portfolioID: number, payload: PortfolioUpsertRequest): Promise<Portfolio> {
  return requestJSON<Portfolio>(`/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function deletePortfolio(portfolioID: number): Promise<void> {
  await fetch(`/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}`, {
    method: "DELETE",
    credentials: "include",
  }).then(async (response) => {
    if (response.ok) {
      return;
    }
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json() as { error?: string; message?: string; };
      if (body.error) {
        message = body.error;
      } else if (body.message) {
        message = body.message;
      }
    } catch {
      // ignore decode failure and fall back to status text
    }
    const error = new Error(message) as APIError;
    error.status = response.status;
    throw error;
  });
}

export async function fetchPortfolioPositionDetail(portfolioID: number, ticker: string): Promise<PortfolioPositionDetail> {
  return requestJSON<PortfolioPositionDetail>(
    `/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}/positions/${encodeURIComponent(ticker)}`,
  );
}

export async function updatePortfolioPositionDetail(
  portfolioID: number,
  ticker: string,
  payload: PortfolioPositionDetailUpsertRequest,
): Promise<PortfolioPositionDetail> {
  return requestJSON<PortfolioPositionDetail>(
    `/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}/positions/${encodeURIComponent(ticker)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchPortfolioPositionReviews(portfolioID: number, ticker: string): Promise<PortfolioReviewListResponse> {
  return requestJSON<PortfolioReviewListResponse>(
    `/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}/positions/${encodeURIComponent(ticker)}/reviews`,
  );
}

export async function createPortfolioPositionReview(
  portfolioID: number,
  ticker: string,
  payload: PortfolioReviewEntryCreateRequest,
): Promise<PortfolioReviewEntry> {
  return requestJSON<PortfolioReviewEntry>(
    `/api/investments/portfolio/portfolios/${encodeURIComponent(String(portfolioID))}/positions/${encodeURIComponent(ticker)}/reviews`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchWatchlist(): Promise<PortfolioResponse> {
  return fetchPortfolio();
}

export async function createWatchlistPortfolio(payload: PortfolioUpsertRequest): Promise<Portfolio> {
  return createPortfolio(payload);
}

export async function updateWatchlistPortfolio(portfolioID: number, payload: PortfolioUpsertRequest): Promise<Portfolio> {
  return updatePortfolio(portfolioID, payload);
}

export async function deleteWatchlistPortfolio(portfolioID: number): Promise<void> {
  return deletePortfolio(portfolioID);
}

export async function fetchInvestmentsAccessStatus(): Promise<InvestmentsAccessResponse> {
  return fetchInvestmentsAccess();
}
